import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

interface Props {
  /** Human label for the screen we're protecting; shows in the fallback. */
  label?: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Local error boundary so a thrown render doesn't blank the whole page.
 * Shows the error message and a stack hint, plus a "reload" button.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', this.props.label ?? 'unknown', error, info);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      return (
        <div className="card" style={{ borderColor: 'var(--warn)', margin: 24 }}>
          <div className="card__head">
            <div>
              <h3 className="card__title" style={{ color: 'var(--warn)' }}>
                <AlertTriangle size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
                Something broke on this page
              </h3>
              <p className="card__sub">
                {this.props.label ? `${this.props.label} ` : ''}failed to render. The error is below.
              </p>
            </div>
          </div>
          <pre
            style={{
              background: 'var(--surface-elev-1, #1a1a1a)',
              padding: 12,
              borderRadius: 6,
              fontSize: 12,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {this.state.error.message}
            {this.state.error.stack ? `\n\n${this.state.error.stack}` : ''}
          </pre>
          <div className="row" style={{ gap: 8, marginTop: 12 }}>
            <button type="button" className="btn btn--ghost btn--sm" onClick={this.reset}>
              Try again
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                if (typeof window !== 'undefined') window.location.reload();
              }}
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
